<template>
  <modal
    id="working-shift-modal"
    size="large"
    v-model="showModal"
    :title="
      this.$fieldTitle(
        this.selectedUrl ? (checkDisable ? 'view' : 'edit') : 'add',
        'work_shift',
        true,
      )
    "
    @submit="submit"
    :hide-submit-button="checkDisable"
    :hide-cancel-button="makeDefault"
    :close-button="false"
    :modal-backdrop="!makeDefault"
    :cancel-btn-label="checkDisable ? $t('close') : $t('cancel')"
    :loading="loading"
    :preloader="preloader"
  >
    <form
      :data-url="selectedUrl ? selectedUrl : apiUrl.WORKING_SHIFTS"
      :class="{ 'disabled-section': checkDisable }"
      method="POST"
      ref="form"
    >
      <app-note
        v-if="this.selectedUrl && !viewOnly"
        class="mb-4"
        :title="$t('note')"
        :notes="
          checkDisable && $can('update_working_shifts')
            ? $t('this_workshift_is_read_only_due_to_attendance_history')
            : $t('working_shift_update_note')
        "
      />
      <app-form-group
        :label="$t('name')"
        type="text"
        v-model="formData.name"
        :placeholder="$placeholder('name', '')"
        :required="true"
        :error-message="$errorMessage(errors, 'name')"
      />
      <div
        class="row"
        v-if="!makeDefault && parseInt(formData.is_default) !== 1"
      >
        <div class="col-md-6">
          <app-form-group
            :disabled="!!formData.is_default"
            :label="$t('start_date')"
            type="date"
            v-model="formData.start_date"
            :placeholder="$placeholder('start_date', '')"
            :required="true"
            :error-message="$errorMessage(errors, 'start_date')"
          />
        </div>
        <div class="col-md-6">
          <app-form-group
            :disabled="!!formData.is_default"
            :label="$t('end_date')"
            type="date"
            v-model="formData.end_date"
            :placeholder="$placeholder('end_date', '')"
            :error-message="$errorMessage(errors, 'end_date')"
          />
        </div>
      </div>

      <div
        class="form-group d-flex flex-column flex-md-row align-items-md-center my-primary"
      >
        <label class="mr-md-3 mb-md-0" v-if="checkDisable">{{
          $fieldTitle("working_shift_type", "")
        }}</label>
        <label class="mr-md-3 mb-md-0" v-else>{{
          $fieldTitle("choose_a_working_shift_type", "")
        }}</label>
        <div>
          <app-input
            type="radio"
            :list="[
              { id: 'regular', value: $t('regular') },
              { id: 'scheduled', value: $t('scheduled') },
            ]"
            v-model="formData.type"
            :error-message="$errorMessage(errors, 'type')"
          />
        </div>
      </div>

      <!--work shift type == regular -->
      <div v-if="formData.type === 'regular'">
        <div class="mt-4">
          <label v-if="checkDisable">{{
            $fieldTitle("regular_week", "")
          }}</label>
          <label v-else>{{ $fieldTitle("set_regular_week", "") }}</label>
          <small class="text-muted">
            ({{ $fieldTitle("set_week_with_fixed_time", "") }})
          </small>
        </div>
        <div class="row">
          <div class="col-md-6">
            <div class="form-group">
              <label>{{ $fieldTitle("start_time", "") }}</label>
              <app-input
                :id="'start_at'"
                type="time"
                v-model="formData.start_at"
                :placeholder="$placeholder('start_time', '')"
                name="start_time"
                :error-message="$errorMessage(errors, 'start_at')"
              />
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group">
              <label>{{ $fieldTitle("end_time", "") }}</label>
              <app-input
                :id="'end-at'"
                type="time"
                v-model="formData.end_at"
                :placeholder="$placeholder('end_time', '')"
                :error-message="$errorMessage(errors, 'end_at')"
              />
            </div>
          </div>
        </div>

        <div class="my-3">
          <h6 v-if="checkDisable" class="text-danger">
            {{ $fieldTitle("weekend_days", "") }}
          </h6>
          <h6 v-else class="text-danger">
            {{ $fieldTitle("select_your_weekend_days", "") }}
          </h6>
        </div>
        <div class="mb-primary">
          <app-input
            type="checkbox"
            :list="weekdays"
            v-model="formData.weekdays"
            radio-checkbox-wrapper="row"
            custom-checkbox-type="checkbox-default col-md-3"
            :error-message="$errorMessage(errors, 'weekdays')"
          />
        </div>
      </div>

      <div v-else>
        <div class="mt-4">
          <label v-if="checkDisable">{{
            $fieldTitle("scheduled_week", "")
          }}</label>
          <label v-else>{{ $fieldTitle("set_scheduled_week", "") }}</label>
          <small class="text-muted">
            ({{
              $fieldTitle(
                "set_week_with_customized_time_and_without_time_and_without_time_it_will_be_weekend",
                "",
              )
            }})
          </small>
        </div>
        <div class="row">
          <div
            v-for="(item, index) in formData.details"
            :key="item.weekday"
            class="col-md-6 mb-4"
          >
            <div class="card working-shift-day-card">
              <div class="card-header working-shift-day-card__header">
                <label class="mb-0">
                  {{ $t(item.weekday) }}
                  <span class="text-muted" v-if="!isWorkingDay(item)">
                    ({{ $t("weekend") }})
                  </span>
                  <span
                    class="badge badge-success ml-2"
                    v-if="
                      item.has_split_shift &&
                      item.periods &&
                      item.periods.length > 1
                    "
                  >
                    {{ item.periods.length }} {{ $t("shift_periods") }}
                  </span>
                </label>
              </div>
              <div class="card-body working-shift-day-card__body">
                <!-- Single shift (backward compatible) -->
                <div v-if="!item.has_split_shift" class="form-row mb-3">
                  <div class="col-md-6">
                    <label class="small text-muted">{{
                      $t("start_time")
                    }}</label>
                    <app-input
                      :id="'start-at-' + index"
                      type="time"
                      v-model="item.start_at"
                      :placeholder="$t('start_time')"
                      :error-message="
                        errorMessageForArray(
                          errors,
                          `details.${index}.start_at`,
                          `details.${index}.end_at`,
                        )
                      "
                    />
                  </div>
                  <div class="col-md-6">
                    <label class="small text-muted">{{ $t("end_time") }}</label>
                    <app-input
                      :id="'end-at-' + index"
                      type="time"
                      v-model="item.end_at"
                      :placeholder="$t('end_time')"
                      :error-message="
                        errorMessageForArray(
                          errors,
                          `details.${index}.end_at`,
                          `details.${index}.start_at`,
                        )
                      "
                    />
                  </div>
                </div>

                <!-- Multiple shift periods (split shift) -->
                <div v-if="item.has_split_shift && item.periods">
                  <div
                    v-for="(period, periodIndex) in item.periods"
                    :key="periodIndex"
                    class="form-row mb-2"
                  >
                    <div class="col-md-5">
                      <label class="small text-muted"
                        >{{ $t("start_time") }} #{{ periodIndex + 1 }}</label
                      >
                      <app-input
                        :id="'period-start-' + index + '-' + periodIndex"
                        type="time"
                        v-model="period.start_at"
                        :placeholder="$t('start_time')"
                      />
                    </div>
                    <div class="col-md-5">
                      <label class="small text-muted"
                        >{{ $t("end_time") }} #{{ periodIndex + 1 }}</label
                      >
                      <app-input
                        :id="'period-end-' + index + '-' + periodIndex"
                        type="time"
                        v-model="period.end_at"
                        :placeholder="$t('end_time')"
                      />
                    </div>
                    <div class="col-md-2 d-flex align-items-end">
                      <button
                        v-if="!checkDisable && item.periods.length > 1"
                        type="button"
                        @click="removePeriod(index, periodIndex)"
                        class="btn btn-sm btn-danger w-100"
                      >
                        <app-icon name="trash" class="size-14" />
                      </button>
                    </div>
                  </div>

                  <small
                    class="text-muted d-block mt-1"
                    v-if="!canAddPeriod(item)"
                  >
                    {{ $t("complete_current_period_before_adding_new_one") }}
                  </small>

                  <!-- Add period button -->
                  <button
                    v-if="!checkDisable"
                    type="button"
                    @click="addPeriod(index)"
                    :disabled="!canAddPeriod(item)"
                    class="btn btn-sm btn-outline-primary mt-2 w-100"
                  >
                    <app-icon name="plus" class="size-14" />
                    {{ $t("add_shift_period") }}
                  </button>
                </div>

                <!-- Toggle split shift button -->
                <div class="mt-3 border-top pt-3" v-if="!checkDisable">
                  <button
                    v-if="!item.has_split_shift && item.start_at && item.end_at"
                    type="button"
                    @click="enableSplitShift(index)"
                    class="btn btn-sm btn-outline-warning w-100"
                  >
                    <app-icon name="clock" class="size-14" />
                    {{ $t("enable_split_shift") }}
                  </button>
                  <button
                    v-else-if="item.has_split_shift"
                    type="button"
                    @click="disableSplitShift(index)"
                    class="btn btn-sm btn-outline-secondary w-100"
                  >
                    <app-icon name="times" class="size-14" />
                    {{ $t("disable_split_shift") }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="checkDisable && breakTimes.length" class="mb-primary">
        <label>{{ $t("break_times") }}</label>
        <p v-for="brk in breakTimes" :key="brk.id">
          <app-icon name="clock" class="size-16" />
          {{ brk.name }} ({{ $t("duration") }} -
          {{ getDurationFromTime(brk.duration) }})
        </p>
      </div>

      <app-form-group-selectable
        v-else
        :label="$fieldLabel('break_time', '')"
        type="multi-select"
        v-model="formData.break_times"
        list-value-field="name"
        :error-message="$errorMessage(errors, 'break_time')"
        :fetch-url="apiUrl.SELECTABLE_BREAK_TIMES"
      />

      <app-form-group
        type="textarea"
        :label="$fieldLabel('description', '')"
        :placeholder="`${checkDisable ? '' : $textAreaPlaceHolder('description')}`"
        v-model="formData.description"
        :required="true"
        :error-message="$errorMessage(errors, 'description')"
      />

      <template
        v-if="parseInt(formData.is_default) !== 1 && !viewOnly && !makeDefault"
      >
        <app-form-group-selectable
          :label="$fieldLabel('department', '')"
          type="multi-select"
          v-model="formData.departments"
          list-value-field="name"
          :error-message="$errorMessage(errors, 'departments')"
          :fetch-url="apiUrl.SELECTABLE_WORK_SHIFT_DEPARTMENTS"
        />

        <app-form-group-selectable
          type="multi-select"
          :label="$t('employee')"
          list-value-field="full_name"
          v-model="formData.users"
          form-group-class="mb-0"
          :error-message="$errorMessage(errors, 'users')"
          :fetch-url="`${apiUrl.SELECTABLE_WORK_SHIFT_USERS}?without=admin&excluded_departments=${formData.departments}&employee=only`"
        />
      </template>
    </form>
  </modal>
</template>

<script>
import FormHelperMixins from "../../../../../common/Mixin/Global/FormHelperMixins";
import ModalMixin from "../../../../../common/Mixin/Global/ModalMixin";
import {
  localToUtc,
  formatDateForServer,
  formatUtcToLocal,
  getDurationFromTime,
} from "../../../../../common/Helper/Support/DateTimeHelper";
import { cloneDeep } from "lodash";
import { errorMessageForArray } from "../../../../../common/Helper/Support/FormHelper";

export default {
  name: "WorkingShiftCreateEditModal",
  mixins: [FormHelperMixins, ModalMixin],
  props: {
    viewOnly: {
      type: Boolean,
      default: false,
    },
    makeDefault: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      errorMessageForArray,
      getDurationFromTime,
      weekdays: [
        { id: "sun", value: this.$t("sunday") },
        { id: "mon", value: this.$t("monday") },
        { id: "tue", value: this.$t("tuesday") },
        { id: "wed", value: this.$t("wednesday") },
        { id: "thu", value: this.$t("thursday") },
        { id: "fri", value: this.$t("friday") },
        { id: "sat", value: this.$t("saturday") },
      ],
      formData: {
        name: "",
        type: "regular",
        departments: [],
        weekdays: [],
        users: [],
        attendances_count: 0,
        details: [
          {
            weekday: "sun",
            start_at: "",
            end_at: "",
            is_weekend: 0,
            has_split_shift: false,
            periods: [],
          },
          {
            weekday: "mon",
            start_at: "",
            end_at: "",
            is_weekend: 0,
            has_split_shift: false,
            periods: [],
          },
          {
            weekday: "tue",
            start_at: "",
            end_at: "",
            is_weekend: 0,
            has_split_shift: false,
            periods: [],
          },
          {
            weekday: "wed",
            start_at: "",
            end_at: "",
            is_weekend: 0,
            has_split_shift: false,
            periods: [],
          },
          {
            weekday: "thu",
            start_at: "",
            end_at: "",
            is_weekend: 0,
            has_split_shift: false,
            periods: [],
          },
          {
            weekday: "fri",
            start_at: "",
            end_at: "",
            is_weekend: 0,
            has_split_shift: false,
            periods: [],
          },
          {
            weekday: "sat",
            start_at: "",
            end_at: "",
            is_weekend: 0,
            has_split_shift: false,
            periods: [],
          },
        ],
      },
      breakTimes: [],
    };
  },
  mounted() {
    if (this.makeDefault) {
      this.formData.name = this.$t("regular_work_shift");
      this.formData.start_date = new Date();
    }
  },
  computed: {
    checkDisable() {
      return this.selectedUrl
        ? this.formData.attendances_count > 0 ||
            !this.$can("update_working_shifts") ||
            this.viewOnly
        : !this.$can("create_working_shifts");
    },
  },
  methods: {
    normalizeTimeForInput(value) {
      if (!value) {
        return "";
      }

      const normalized = String(value).trim().replace(" ", ":");
      const match = normalized.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);

      if (!match) {
        return "";
      }

      const hours = match[1].padStart(2, "0");
      return `${hours}:${match[2]}`;
    },
    isPeriodComplete(period) {
      return !!(period && period.start_at && period.end_at);
    },
    canAddPeriod(item) {
      if (!item || !item.periods || !item.periods.length) {
        return false;
      }

      return item.periods.every((period) => this.isPeriodComplete(period));
    },
    isWorkingDay(item) {
      if (!item) {
        return false;
      }

      if (item.has_split_shift && item.periods && item.periods.length) {
        return item.periods.some((period) => this.isPeriodComplete(period));
      }

      return !!(item.start_at && item.end_at);
    },
    submit() {
      this.loading = true;
      this.message = "";
      this.errors = {};

      let formData = {};
      if (this.formData.type === "regular") {
        formData = this.prepareRegularType(this.formData);
      } else {
        formData = this.prepareSchedulerType(this.formData);
      }

      formData.start_date = formatDateForServer(formData.start_date);
      formData.end_date = formatDateForServer(formData.end_date);
      this.makeDefault ? (formData.is_default = 1) : null;

      this.save(formData);
    },
    afterSuccess({ data }) {
      this.formData = {};
      $("#working-shift-modal").modal("hide");
      this.toastAndReload(data.message, "working-shift-table");
    },
    afterSuccessFromGetEditData({ data }) {
      this.formData = data;
      this.formData.start_date = data.start_date
        ? new Date(data.start_date)
        : "";
      this.formData.end_date = data.end_date ? new Date(data.end_date) : "";
      this.formData.start_at = this.normalizeTimeForInput(
        formatUtcToLocal(data.start_at),
      );
      this.formData.end_at = this.normalizeTimeForInput(
        formatUtcToLocal(data.end_at),
      );
      this.formData.weekdays = data.details
        .filter((week) => {
          return parseInt(week.is_weekend);
        })
        .map((week) => {
          return week.weekday;
        });

      this.formData.details = data.details.map((details) => {
        let detail = {
          ...details,
          start_at: this.normalizeTimeForInput(
            formatUtcToLocal(details.start_at),
          ),
          end_at: this.normalizeTimeForInput(formatUtcToLocal(details.end_at)),
          has_split_shift: details.has_split_shift || false,
          periods: [],
        };

        // Load periods if they exist
        if (details.periods && details.periods.length > 0) {
          detail.periods = details.periods.map((period) => ({
            start_at: this.normalizeTimeForInput(
              formatUtcToLocal(period.start_at),
            ),
            end_at: this.normalizeTimeForInput(formatUtcToLocal(period.end_at)),
            sequence: period.sequence,
          }));
        }

        return detail;
      });

      this.formData.departments = this.collection(data.departments)
        .pluck()
        .concat(this.collection(data.upcoming_departments).pluck());
      this.formData.users = this.collection(data.users)
        .pluck()
        .concat(this.collection(data.upcoming_users).pluck());
      this.breakTimes = data.break_times;
      this.formData.break_times = this.collection(data.break_times).pluck();
      this.preloader = false;
    },
    prepareRegularType(formData) {
      let data = cloneDeep(formData);

      if (!(data.start_at && data.end_at)) {
        return data;
      }

      data.details.map((day) => {
        if (data.weekdays.includes(day.weekday)) {
          day.end_at = null;
          day.start_at = null;
          day.is_weekend = 1;
          return day;
        }

        day.end_at = localToUtc(data.end_at);
        day.start_at = localToUtc(data.start_at);
        day.is_weekend = data.weekdays.includes(day.weekday) ? 1 : 0;
        return day;
      });

      return data;
    },
    prepareSchedulerType(formData) {
      let data = cloneDeep(formData);
      data.details.map((day) => {
        // Convert periods if using split shift
        if (day.has_split_shift && day.periods && day.periods.length > 0) {
          const validPeriods = day.periods
            .filter((period) => this.isPeriodComplete(period))
            .map((period, periodIndex) => ({
              start_at: this.normalizeTimeForInput(period.start_at),
              end_at: this.normalizeTimeForInput(period.end_at),
              sequence: period.sequence || periodIndex + 1,
            }));

          day.periods = validPeriods.map((period) => ({
            start_at: localToUtc(period.start_at),
            end_at: localToUtc(period.end_at),
            sequence: period.sequence,
          }));

          if (day.periods.length) {
            day.start_at = day.periods[0].start_at;
            day.end_at = day.periods[day.periods.length - 1].end_at;
          } else {
            day.has_split_shift = false;
            day.start_at = null;
            day.end_at = null;
          }
        } else {
          // Single period - convert legacy start_at/end_at
          day.start_at = day.start_at ? localToUtc(day.start_at) : null;
          day.end_at = day.end_at ? localToUtc(day.end_at) : null;
        }

        day.is_weekend = !!(day.start_at && day.end_at) ? 0 : 1;
        return day;
      });
      return data;
    },
    enableSplitShift(dayIndex) {
      const day = this.formData.details[dayIndex];
      day.has_split_shift = true;

      // Initialize periods with the current single shift
      day.periods = [
        {
          start_at: this.normalizeTimeForInput(day.start_at),
          end_at: this.normalizeTimeForInput(day.end_at),
          sequence: 1,
        },
      ];
    },
    disableSplitShift(dayIndex) {
      const day = this.formData.details[dayIndex];
      day.has_split_shift = false;

      // Move first period back to single shift
      if (day.periods && day.periods.length > 0) {
        day.start_at = day.periods[0].start_at;
        day.end_at = day.periods[0].end_at;
      }

      day.periods = [];
    },
    addPeriod(dayIndex) {
      const day = this.formData.details[dayIndex];
      if (!day.periods) {
        day.periods = [];
      }

      if (!this.canAddPeriod(day)) {
        return;
      }

      const newSequence = day.periods.length + 1;
      day.periods.push({
        start_at: "",
        end_at: "",
        sequence: newSequence,
      });
    },
    removePeriod(dayIndex, periodIndex) {
      const day = this.formData.details[dayIndex];
      if (day.periods) {
        if (day.periods.length <= 2) {
          const remainingPeriod = day.periods.find(
            (period, index) => index !== periodIndex,
          );

          day.has_split_shift = false;
          day.start_at = remainingPeriod ? remainingPeriod.start_at : "";
          day.end_at = remainingPeriod ? remainingPeriod.end_at : "";
          day.periods = [];

          return;
        }

        day.periods.splice(periodIndex, 1);
        day.periods = day.periods.map((period, index) => ({
          ...period,
          sequence: index + 1,
        }));
      }
    },
  },
};
</script>

<style lang="scss">
.working-shift-day-card {
  background-color: var(--default-card-bg);
  border-color: var(--default-border-color);
  box-shadow: var(--default-box-shadow);
}

.working-shift-day-card__header {
  background-color: transparent;
  border-bottom-color: var(--default-border-color);
  color: var(--default-font-color);
}

.working-shift-day-card__body {
  color: var(--default-font-color);
}

.working-shift-day-card__body .border-top {
  border-top-color: var(--default-border-color) !important;
}
</style>
